import React, { useState } from "react";
import styled from "styled-components";
import { State, PromotionDetails, TimeSlot } from "../../types";
import {
  PromotionSelector,
  Button,
  IconButton,
  BookingSummary,
  Logo,
  Title,
  Icon,
} from "../../components";
import DateHelper from "../../helpers/DateHelper";
import { defaultThemeStyles } from "../../mocks";
import { useTranslation } from "react-i18next";
import { ArrowLeftSolid, Warning } from "../../assets/icons";
import { Promotion } from "../../domain_objects/AvailabilitySearchResponse";
import SelectedVenueDetailsHelper from "../../helpers/SelectedVenueDetailsHelper";
import ReturnTableNotice from "../../components/ReturnTableNotice/ReturnTableNotice";

const StyledPageContainer = styled.div`
  display: grid;
  row-gap: 16px;
`;

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 30px;
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  flex-grow: 1;
`;

const StyledPromotionList = styled.div`
  display: grid;
  row-gap: 8px;
`;

const RequiredPromotionMessageDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 4px;
  font-size: 13px;
  font-weight: 700;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export type PromotionPageProps = {
  state: State;
  nextPage: (payload: Partial<State>) => void;
  prevPage: (payload: Partial<State>) => void;
};

export const PromotionPage: React.FC<PromotionPageProps> = ({
  state,
  nextPage,
  prevPage,
}) => {
  const { promotionDetails, bookingDetails, crossSellSetup } = state;
  const { t } = useTranslation();
  const [selectedPromotion, setSelectedPromotion] =
    useState<PromotionDetails | null>(promotionDetails ?? null);
  const [displayRequiredPromotionMessage, setDisplayRequiredPromotionMessage] =
    useState("");
  const [availableUntil, setAvailableUntil] = useState<
    null | string | undefined
  >(
    state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired
      ? state.promotionDetails?.leaveTime?.length
        ? state.promotionDetails.leaveTime
        : state.bookingDetails.timeSlot?.TimeSlot.LeaveTime
      : null
  );

  const completePage = () => {
    if (
      !state.bookingDetails.timeSlot?.TimeSlot.HasStandardAvailability &&
      selectedPromotion == null
    ) {
      setDisplayRequiredPromotionMessage(t("ErrorPromotionNotSelected"));
    } else {
      const updatedState = selectedPromotion
        ? { ...state, promotionDetails: selectedPromotion }
        : state;
      if (!selectedPromotion) {
        delete updatedState.promotionDetails;
      }

      nextPage(updatedState);
    }
  };

  const backButtonClick = () => {
    const updatedState = state;
    delete updatedState.promotionDetails;

    if (
      state.crossSellSetup?.UseNewSkipTimeSelectFlow &&
      state.availabilityType === "full"
    ) {
      delete updatedState.bookingDetails.timeSlot;
      updatedState.availabilityResponses = [];
      updatedState.searchedCrossSellVenues = [];
      updatedState.availableCrossSellVenues = [];
    }

    prevPage(updatedState);
  };

  const getPromotions = (): PromotionDetails[] => {
    const selectedTimeSlot = state.bookingDetails.timeSlot;
    const availablePromotions =
      selectedTimeSlot?.TimeSlot.AvailablePromotions ?? [];
    const availablePromotionIds = availablePromotions?.map(
      (promotion) => promotion.Id
    );

    // match the microsite name to get relevant promotions
    const venuePromotions = state.isCrossSellChosen
      ? getCrossellVenuePromotions(selectedTimeSlot)
      : getOriginalVenuePromotions(selectedTimeSlot);

    // filter and map the promotions that are available for the selected time slot to PromotionDetails type
    const promotions = availablePromotionIds.map((promotionId) => {
      const promotion = venuePromotions.find(
        (venuePromotion) => venuePromotion.Id === promotionId
      );

      if (promotion) {
        const availabilitySearchPromotion =
          selectedTimeSlot?.TimeSlot.AvailablePromotions?.find(
            (promotion) => promotion.Id === promotionId
          );

        const promotionDetails: PromotionDetails = {
          promotionName: promotion.Name,
          promotionDescription: promotion.Description,
          promotionId: promotion.Id,
          menuName: promotion.MenuName,
          menuUrl: promotion.MenuUrl,
          leaveTime: availabilitySearchPromotion?.LeaveTime || "",
        };
        return promotionDetails;
      }
    });

    return promotions.filter(
      (promotion) => promotion !== undefined
    ) as PromotionDetails[];
  };

  const getOriginalVenuePromotions = (
    selectedTimeSlot: TimeSlot | undefined
  ): Promotion[] => {
    return (
      state.availabilityResponses?.find((availabilityResponse) => {
        if (
          availabilityResponse.MicrositeName === selectedTimeSlot?.MicrositeName
        ) {
          return availabilityResponse.Promotions;
        }
      })?.Promotions ?? []
    );
  };

  const getCrossellVenuePromotions = (
    selectedTimeSlot: TimeSlot | undefined
  ): Promotion[] => {
    return (
      state.searchedCrossSellVenues?.find((scsv) => {
        if (
          scsv.CrossSellVenue.MicrositeName === selectedTimeSlot?.MicrositeName
        ) {
          return scsv.AvailabilitySearchResponse;
        }
      })?.AvailabilitySearchResponse.Promotions ?? []
    );
  };

  const selectPromotion = (promotion: PromotionDetails) => {
    if (promotion.promotionId === selectedPromotion?.promotionId) {
      setSelectedPromotion(null);
      setAvailableUntil(
        bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired
          ? bookingDetails.timeSlot?.TimeSlot.LeaveTime
          : null
      );
    } else {
      setSelectedPromotion(promotion);
      setAvailableUntil(promotion.leaveTime);
    }
  };

  return (
    <StyledPageContainer>
      <Logo styles={crossSellSetup?.ThemeStyles || defaultThemeStyles} />
      <StyledHeader>
        <IconButton
          styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}
          icon={ArrowLeftSolid}
          ariaLabel="Previous"
          onClick={backButtonClick}
          dataId="back-button"
        />
        <StyledTitle styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}>
          {state.bookingDetails.timeSlot?.TimeSlot.HasStandardAvailability
            ? t("PleaseSelectOption")
            : t("OptionRequired")}
        </StyledTitle>
      </StyledHeader>
      <StyledPromotionList data-id="promotions-list">
        {getPromotions().map((promotion, index) => (
          <PromotionSelector
            key={index}
            styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}
            promotion={promotion}
            isPromotionSelected={
              selectedPromotion?.promotionName === promotion.promotionName
            }
            setSelectedPromotion={() => selectPromotion(promotion)}
          />
        ))}
      </StyledPromotionList>
      <FooterContainer>
        <BookingSummary
          flashBookingSummaryPanel={state.flashBookingSummaryPanel || false}
          date={bookingDetails.selectedDate}
          numberOfCovers={bookingDetails.partySize}
          timeslot={
            bookingDetails.timeSlot
              ? DateHelper.sliceTimeSlot(bookingDetails.timeSlot)
              : undefined
          }
          styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}
          {...(state.isCrossSellChosen && {
            newProvider: {
              name:
                SelectedVenueDetailsHelper.getVenueDetails(state).name || "",
              location:
                SelectedVenueDetailsHelper.getVenueDetails(state).location ||
                "",
              locationUrl:
                SelectedVenueDetailsHelper.getVenueDetails(state).url || "",
            },
          })}
        />
        {availableUntil &&
          state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired &&
          state.bookingDetails.timeSlot?.TimeSlot.LeaveTime && (
            <ReturnTableNotice
              styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}
              leaveTime={availableUntil}
              isPromotion={!!selectedPromotion?.leaveTime}
            />
          )}
        {displayRequiredPromotionMessage && (
          <RequiredPromotionMessageDiv data-id="required-message">
            <Icon icon={Warning} />
            <label>{displayRequiredPromotionMessage}</label>
          </RequiredPromotionMessageDiv>
        )}
        <Button
          styles={crossSellSetup?.ThemeStyles || defaultThemeStyles}
          onClick={completePage}
          rightArrowIcon
          dataId="next-button"
        >
          {t("Next")}
        </Button>
      </FooterContainer>
    </StyledPageContainer>
  );
};

export default PromotionPage;
